import { useEffect, useState } from 'react';
import { Eye, SignOut } from '@phosphor-icons/react';
import { safeLocalStorage } from '@/lib/safeStorage';
import { STORAGE_KEYS } from '@/lib/constants';

/**
 * A persistent bar shown whenever the current session is a platform-operator "view as" (RUT-3105),
 * so it is never mistaken for the real user's own login. Exiting clears the session and returns to
 * a clean state. Renders nothing for ordinary logins.
 */
export function ViewAsBanner() {
  const [info, setInfo] = useState<{ name: string; org: string } | null>(null);

  useEffect(() => {
    const raw = safeLocalStorage.getItem(STORAGE_KEYS.VIEW_AS);
    if (!raw) return;
    try {
      setInfo(JSON.parse(raw));
    } catch {
      setInfo({ name: 'a user', org: '' });
    }
  }, []);

  if (!info) return null;

  const exit = () => {
    safeLocalStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
    safeLocalStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    safeLocalStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    safeLocalStorage.removeItem(STORAGE_KEYS.VIEW_AS);
    safeLocalStorage.setItem(STORAGE_KEYS.HAS_LOGGED_OUT, 'true');
    // Try to close the tab (it was opened by Admin Insights); fall back to the login screen.
    window.close();
    window.location.replace('/login');
  };

  // A solid band, not a wash: this sits on the dark `bg-nav` chrome and has to be
  // impossible to miss (RUT-3732 briefly made it a near-white strip that read as
  // ordinary chrome). `--warning` with a near-black label is 5.54:1; the white
  // `--warning-foreground` would be 3.57:1 and fail.
  return (
    <div className="flex items-center justify-center gap-3 bg-warning px-4 py-1.5 text-center text-sm font-medium text-stone-950">
      <Eye className="h-4 w-4 shrink-0" weight="fill" />
      <span className="truncate">
        Viewing as <strong>{info.name}</strong>
        {info.org ? ` · ${info.org}` : ''} — read-only operator session
      </span>
      <button
        type="button"
        onClick={exit}
        className="inline-flex shrink-0 items-center gap-1 rounded-md bg-stone-950/10 px-2 py-0.5 text-xs font-semibold hover:bg-stone-950/20"
      >
        <SignOut className="h-3.5 w-3.5" />
        Exit
      </button>
    </div>
  );
}
